import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import CmWizardPathNav from "../components/CmWizardPathNav.js";
import { relockFinancialQueries } from "../lib/query-invalidation.js";

interface SkuRow {
  id: string;
  skuCode: string;
  sellingPrice: string | null;
  sellingCurrency: string | null;
  isBundle: boolean;
  active: boolean;
}

interface CmInput {
  id: string;
  skuId: string;
  cogs: string;
  fulfillmentCost: string;
  pickPackFee: string;
  storageFeePerMonth: string;
  returnsProcessingCost: string;
  inboundFreightAllocation: string;
  processingFeePct: string;
  processingFeeFixed: string;
  refundRatePct: string;
  laborPerUnit: string;
  currency: string;
  effectiveDate: string;
  active: boolean;
}

interface DraftRow {
  skuId: string;
  cogs: string;
  fulfillmentCost: string;
  pickPackFee: string;
  processingFeePct: string;
  laborPerUnit: string;
  currency: string;
}

/**
 * Phase 1.6 CM Wizard — manual path only. Doc upload + spreadsheet land
 * in 1.7. Shows the SKU list with editable fields + Save button.
 *
 * "Universal Apply" UX from Spec Section 6 is handled in the multi-select
 * toolbar — select rows, then "Apply value to selected" buttons next to
 * each input.
 */
export default function CmWizardPage(): React.ReactElement {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, DraftRow>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const skusQuery = useQuery({
    queryKey: ["cm-wizard", "skus"],
    queryFn: () => api.get<{ skus: SkuRow[] }>("/cm-wizard/skus"),
  });

  // Pre-load existing CM inputs for each SKU.
  useEffect(() => {
    if (!skusQuery.data) return;
    void (async () => {
      const next: Record<string, DraftRow> = {};
      for (const sku of skusQuery.data.skus) {
        const result = await api.get<{ cmInput: CmInput | null }>(
          `/cm-wizard/skus/${sku.id}/cm-input`,
        );
        const existing = result.cmInput;
        next[sku.id] = {
          skuId: sku.id,
          cogs: existing?.cogs ?? "",
          fulfillmentCost: existing?.fulfillmentCost ?? "",
          pickPackFee: existing?.pickPackFee ?? "",
          processingFeePct: existing?.processingFeePct ?? "0.029",
          laborPerUnit: existing?.laborPerUnit ?? "",
          currency: existing?.currency ?? sku.sellingCurrency ?? "USD",
        };
      }
      setDrafts(next);
    })();
  }, [skusQuery.data, api]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api.post("/cm-wizard/manual", {
        applyTo: "forward",
        drafts: Object.values(drafts)
          .filter((d) => d.cogs !== "" && Number(d.cogs) >= 0)
          .map((d) => ({
            skuId: d.skuId,
            cogs: Number(d.cogs),
            fulfillmentCost: numberOrZero(d.fulfillmentCost),
            pickPackFee: numberOrZero(d.pickPackFee),
            processingFeePct: numberOrZero(d.processingFeePct),
            laborPerUnit: numberOrZero(d.laborPerUnit),
            currency: d.currency,
          })),
      }),
    onSuccess: () => {
      relockFinancialQueries(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["cm-wizard"] });
      window.alert("Saved.");
    },
  });

  const updateDraft = (skuId: string, field: keyof DraftRow, value: string): void => {
    setDrafts((prev) => {
      const existing = prev[skuId];
      if (!existing) return prev;
      return { ...prev, [skuId]: { ...existing, [field]: value } };
    });
  };

  const applyToSelected = (field: keyof DraftRow, value: string): void => {
    setDrafts((prev) => {
      const next = { ...prev };
      for (const id of selected) {
        const row = next[id];
        if (row) next[id] = { ...row, [field]: value };
      }
      return next;
    });
  };

  const toggleSelected = (skuId: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(skuId)) next.delete(skuId);
      else next.add(skuId);
      return next;
    });
  };

  const skus = skusQuery.data?.skus ?? [];

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: 24 }}>CM Wizard</h1>
      <p style={{ color: "var(--body)", marginTop: 6 }}>
        Set your contribution margin per SKU. The numbers under each card on your
        dashboard depend on this.
      </p>

      <div style={{ marginTop: 24 }}>
        <CmWizardPathNav />
      </div>

      <div style={headerStyles.row}>
        <div />
        <button
          style={headerStyles.saveButton}
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending || Object.keys(drafts).length === 0}
        >
          {saveMutation.isPending ? "Saving…" : "Save all"}
        </button>
      </div>

      {selected.size > 0 && (
        <div style={toolbarStyles.bar}>
          <strong>{selected.size} selected</strong>
          <ApplyChip
            label="Apply COGS"
            onApply={(v) => applyToSelected("cogs", v)}
          />
          <ApplyChip
            label="Apply fulfillment"
            onApply={(v) => applyToSelected("fulfillmentCost", v)}
          />
          <ApplyChip
            label="Apply pick/pack"
            onApply={(v) => applyToSelected("pickPackFee", v)}
          />
          <ApplyChip
            label="Apply processing %"
            onApply={(v) => applyToSelected("processingFeePct", v)}
            placeholder="e.g. 0.029"
          />
        </div>
      )}

      {skusQuery.isLoading && <p style={{ marginTop: 24 }}>Loading SKUs…</p>}

      {skus.length === 0 && !skusQuery.isLoading && (
        <p style={{ marginTop: 24, color: "var(--body)" }}>
          No SKUs yet. Sync your catalog from the Connections page.
        </p>
      )}

      {skus.length > 0 && (
        <div style={tableStyles.scroll}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}></th>
                <th style={tableStyles.th}>SKU</th>
                <th style={tableStyles.th}>Price</th>
                <th style={tableStyles.th}>COGS</th>
                <th style={tableStyles.th}>Fulfillment</th>
                <th style={tableStyles.th}>Pick/pack</th>
                <th style={tableStyles.th}>Processing %</th>
                <th style={tableStyles.th}>Labor</th>
                <th style={tableStyles.th}>CM / unit</th>
              </tr>
            </thead>
            <tbody>
              {skus.map((sku) => {
                const draft = drafts[sku.id];
                if (!draft) return null;
                const price = sku.sellingPrice ? Number(sku.sellingPrice) : 0;
                const costs =
                  numberOrZero(draft.cogs) +
                  numberOrZero(draft.fulfillmentCost) +
                  numberOrZero(draft.pickPackFee) +
                  numberOrZero(draft.laborPerUnit) +
                  price * numberOrZero(draft.processingFeePct);
                const cmPerUnit = price - costs;
                return (
                  <tr key={sku.id}>
                    <td style={tableStyles.td}>
                      <input
                        type="checkbox"
                        checked={selected.has(sku.id)}
                        onChange={() => toggleSelected(sku.id)}
                      />
                    </td>
                    <td style={tableStyles.td}>{sku.skuCode}</td>
                    <td style={tableStyles.td}>
                      {sku.sellingPrice ? formatPrice(price, sku.sellingCurrency ?? "USD") : "—"}
                    </td>
                    <td style={tableStyles.td}>
                      <NumberInput
                        value={draft.cogs}
                        onChange={(v) => updateDraft(sku.id, "cogs", v)}
                      />
                    </td>
                    <td style={tableStyles.td}>
                      <NumberInput
                        value={draft.fulfillmentCost}
                        onChange={(v) => updateDraft(sku.id, "fulfillmentCost", v)}
                      />
                    </td>
                    <td style={tableStyles.td}>
                      <NumberInput
                        value={draft.pickPackFee}
                        onChange={(v) => updateDraft(sku.id, "pickPackFee", v)}
                      />
                    </td>
                    <td style={tableStyles.td}>
                      <NumberInput
                        value={draft.processingFeePct}
                        onChange={(v) => updateDraft(sku.id, "processingFeePct", v)}
                        step="0.001"
                      />
                    </td>
                    <td style={tableStyles.td}>
                      <NumberInput
                        value={draft.laborPerUnit}
                        onChange={(v) => updateDraft(sku.id, "laborPerUnit", v)}
                      />
                    </td>
                    <td
                      style={{
                        ...tableStyles.td,
                        color: cmPerUnit > 0 ? "var(--good)" : "var(--bad)",
                        fontWeight: 600,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {price > 0 ? formatPrice(cmPerUnit, sku.sellingCurrency ?? "USD") : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NumberInput({
  value,
  onChange,
  step = "0.01",
}: {
  value: string;
  onChange: (v: string) => void;
  step?: string;
}): React.ReactElement {
  return (
    <input
      type="number"
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={inputStyles.input}
    />
  );
}

function ApplyChip({
  label,
  onApply,
  placeholder,
}: {
  label: string;
  onApply: (value: string) => void;
  placeholder?: string;
}): React.ReactElement {
  const [v, setV] = useState("");
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      <input
        type="number"
        placeholder={placeholder ?? "0.00"}
        value={v}
        onChange={(e) => setV(e.target.value)}
        style={{ ...inputStyles.input, width: 90 }}
      />
      <button
        onClick={() => {
          if (v !== "") onApply(v);
        }}
        style={toolbarStyles.applyButton}
      >
        {label}
      </button>
    </div>
  );
}

function numberOrZero(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatPrice(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

const headerStyles: Record<string, React.CSSProperties> = {
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  saveButton: {
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    border: "none",
    padding: "10px 20px",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
};

const toolbarStyles: Record<string, React.CSSProperties> = {
  bar: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    background: "var(--surface-2)",
    padding: "12px 16px",
    borderRadius: 8,
    marginTop: 16,
    flexWrap: "wrap",
  },
  applyButton: {
    background: "transparent",
    color: "var(--good)",
    border: "1px solid var(--good)",
    padding: "5px 10px",
    borderRadius: 6,
    fontSize: 12,
    cursor: "pointer",
  },
};

const inputStyles: Record<string, React.CSSProperties> = {
  input: {
    background: "var(--surface-2)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    padding: "6px 8px",
    borderRadius: 4,
    fontSize: 13,
    width: 90,
    fontVariantNumeric: "tabular-nums",
    fontFamily: "inherit",
  },
};

const tableStyles: Record<string, React.CSSProperties> = {
  scroll: {
    marginTop: 24,
    overflowX: "auto",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 10,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "12px 14px",
    fontSize: 11,
    color: "var(--body)",
    fontWeight: 500,
    borderBottom: "1px solid var(--border)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 14px",
    fontSize: 13,
    borderBottom: "1px solid var(--border)",
    whiteSpace: "nowrap",
  },
};
